import {
  DocumentationPageApproval,
  DocumentationPageSnapshot,
  DocumentationPageV2,
  ElementGroup,
  ElementGroupSnapshot,
} from "@supernova-studio/model";
import * as Y from "yjs";
import { ZodSchema, ZodTypeDef } from "zod";
import { DocumentationHierarchySettings } from "../design-system-content";
import { VersionRoomBaseYDocState } from "./types";

type YJSSerializable = { id: string };

export class VersionRoomBaseYDoc {
  private readonly yDoc: Y.Doc;

  constructor(yDoc: Y.Doc) {
    this.yDoc = yDoc;
  }

  getState(): VersionRoomBaseYDocState {
    // For backward compatibility we are using groups array to determine if Y doc content
    // was loaded: if the content was loaded there has to be at least one (root) group in
    // the doc. To be improved later if needed.

    const groups = this.getGroups();
    const isLoaded = !!groups.length;

    return {
      isLoaded,
      groups,
      pages: this.getPages(),
      approvals: this.getApprovals(),
      groupSnapshots: this.getGroupSnapshots(),
      pageContentHashes: this.getDocumentationPageContentHashes(),
      pageSnapshots: this.getPageSnapshots(),
      settings: this.getDocumentationInternalSettings(),
      pageLiveblockRoomIds: this.getDocumentationPageLiveblocksRoomIds(),
      executedTransactionIds: this.getExecutedTransactionIds(),
    };
  }

  //
  // Pages
  //

  getPages(): DocumentationPageV2[] {
    return this.getObjects(this.pagesYMap, DocumentationPageV2);
  }

  updatePages(pages: DocumentationPageV2[]) {
    pages = pages.map(page => {
      // We remove blocks from the payload here because it will not get parsed anyway
      return {
        ...page,
        data: {
          configuration: page.data.configuration,
        },
      };
    });

    this.setObjects(this.pagesYMap, pages);
  }

  deletePages(ids: string[]) {
    this.deleteObjects(this.pagesYMap, ids);
  }

  private get pagesYMap() {
    return this.yDoc.getMap<object>("documentationPages");
  }

  //
  // Groups
  //

  getGroups(): ElementGroup[] {
    return this.getObjects(this.groupsYMap, ElementGroup);
  }

  updateGroups(groups: ElementGroup[]) {
    this.setObjects(this.groupsYMap, groups);
  }

  deleteGroups(ids: string[]) {
    this.deleteObjects(this.groupsYMap, ids);
  }

  private get groupsYMap() {
    return this.yDoc.getMap<object>("documentationGroups");
  }

  //
  // Documentation internal settings
  //

  getDocumentationInternalSettings(): DocumentationHierarchySettings {
    const map = this.internalSettingsYMap;

    const rawSettings: Record<keyof DocumentationHierarchySettings, any> = {
      routingVersion: map.get("routingVersion"),
      isDraftFeatureAdopted: map.get("isDraftFeatureAdopted") ?? false,
      isApprovalFeatureEnabled: map.get("isApprovalFeatureEnabled") ?? false,
      approvalRequiredForPublishing: map.get("approvalRequiredForPublishing") ?? false,
    };

    const settingsParseResult = DocumentationHierarchySettings.safeParse(rawSettings);
    if (!settingsParseResult.success) {
      return {
        routingVersion: "2",
        isDraftFeatureAdopted: false,
        isApprovalFeatureEnabled: false,
        approvalRequiredForPublishing: false,
      };
    }

    return settingsParseResult.data;
  }

  updateDocumentationInternalSettings(settings: Partial<DocumentationHierarchySettings>) {
    const map = this.internalSettingsYMap;
    map.set("routingVersion", settings.routingVersion ?? map.get("routingVersion"));
    map.set("isDraftFeatureAdopted", settings.isDraftFeatureAdopted ?? map.get("isDraftFeatureAdopted"));
    map.set("isApprovalFeatureEnabled", settings.isApprovalFeatureEnabled ?? map.get("isApprovalFeatureEnabled"));
    map.set(
      "approvalRequiredForPublishing",
      settings.approvalRequiredForPublishing ?? map.get("approvalRequiredForPublishing")
    );
  }

  private get internalSettingsYMap() {
    return this.yDoc.getMap<unknown>("documentationInternalSettings");
  }

  //
  // Documentation page snapshot
  //

  getPageSnapshots(): DocumentationPageSnapshot[] {
    return this.getObjects(this.documentationPagePublishedStatesYMap, DocumentationPageSnapshot);
  }

  updatePageSnapshots(snapshots: DocumentationPageSnapshot[]) {
    this.setObjects(this.documentationPagePublishedStatesYMap, snapshots);
  }

  deletePageSnapshots(ids: string[]) {
    this.deleteObjects(this.documentationPagePublishedStatesYMap, ids);
  }

  private get documentationPagePublishedStatesYMap() {
    return this.yDoc.getMap<object>("documentationPageSnapshots");
  }

  //
  // Documentation group snapshots
  //

  getGroupSnapshots(): ElementGroupSnapshot[] {
    return this.getObjects(this.documentationGroupPublishedStatesYMap, ElementGroupSnapshot);
  }

  updateGroupSnapshots(snapshots: ElementGroupSnapshot[]) {
    this.setObjects(this.documentationGroupPublishedStatesYMap, snapshots);
  }

  deleteGroupSnapshots(ids: string[]) {
    this.deleteObjects(this.documentationGroupPublishedStatesYMap, ids);
  }

  private get documentationGroupPublishedStatesYMap() {
    return this.yDoc.getMap<object>("documentationGroupSnapshots");
  }

  //
  // Utils
  //

  private getObjects<T extends YJSSerializable, I>(map: Y.Map<object>, schema: ZodSchema<T, ZodTypeDef, I>): T[] {
    const results: T[] = [];
    map.forEach(object => results.push(schema.parse(object)));
    return results;
  }

  private setObjects<T extends YJSSerializable>(map: Y.Map<object>, objects: T[]) {
    objects.forEach(o => map.set(o.id, JSON.parse(JSON.stringify(o))));
  }

  private deleteObjects(map: Y.Map<object>, ids: string[]) {
    ids.forEach(id => map.delete(id));
  }

  //
  // Documentation page content hashes
  //

  getDocumentationPageContentHashes(): Record<string, string> {
    const map = this.documentationPageContentHashesYMap;

    const result: Record<string, string> = {};
    map.forEach((hash, key) => {
      if (typeof hash === "string") result[key] = hash;
    });

    return result;
  }

  getDocumentationPageLiveblocksRoomIds(): Record<string, string> {
    const map = this.documentationPageLiveblocksRoomIdsYMap;

    const result: Record<string, string> = {};
    map.forEach((hash, key) => {
      if (typeof hash === "string") result[key] = hash;
    });

    return result;
  }

  private get documentationPageLiveblocksRoomIdsYMap() {
    return this.yDoc.getMap<string>("documentationPageLiveblocksRoomIds");
  }

  updateDocumentationPageContentHashes(hashes: Record<string, string>) {
    const map = this.documentationPageContentHashesYMap;
    Object.entries(hashes).forEach(([key, hash]) => map.set(key, hash));
  }

  private get documentationPageContentHashesYMap() {
    return this.yDoc.getMap<string>("documentationPageHashes");
  }

  //
  // Approval states
  //

  updateApprovalStates(updates: DocumentationPageApproval[]) {
    this.setObjects(this.documentationPageApprovalsMap, updates);
  }

  removeApprovalStates(ids: string[]) {
    this.deleteObjects(this.documentationPageApprovalsMap, ids);
  }

  getApprovals() {
    return this.getObjects(this.documentationPageApprovalsMap, DocumentationPageApproval);
  }

  private get documentationPageApprovalsMap() {
    return this.yDoc.getMap<object>("documentationPageApprovals");
  }

  //
  // Executed transactions
  //

  updateExecutedTransactionIds(transactionIds: string[]) {
    transactionIds = Array.from(new Set(transactionIds));
    if (!transactionIds.length) return;

    const array = this.executedTransactionIdsArray;

    // Append transactions at the end
    array.push(transactionIds);

    // Trim from the beginning in case it contains more than 100
    if (array.length > 100) {
      array.delete(0, array.length - 100);
    }
  }

  getExecutedTransactionIds(): string[] {
    const array = this.executedTransactionIdsArray;

    // Y.Array doesn't support filtering
    const transactionIds: string[] = [];
    array.forEach(e => typeof e === "string" && transactionIds.push(e));

    return transactionIds;
  }

  private get executedTransactionIdsArray() {
    return this.yDoc.getArray<unknown>("executedTransactionIds");
  }
}
